import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { MessageCircle, Phone, Send, Menu, X, LogOut, Voicemail } from 'lucide-react'
import { Amplify } from 'aws-amplify';
import { signIn, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { EmojiPickerButton } from './components/EmojiPicker';
import { TemplatePicker } from './components/TemplatePicker';
import { KeywordPickerV2 } from './components/KeywordPickerV2';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary';
import CallModal from './components/CallModal';
import BrowserCallComposeModal from './components/BrowserCallComposeModal';
import { ConversationsView } from './components/ConversationsView';
import { VoicemailsView } from './components/VoicemailsView';
import { PhoneSelector } from './components/PhoneSelector';
import { MobileBlocker } from './components/MobileBlocker';
import { API_BASE_URL, WS_URL, API_ENDPOINTS } from './config';

// Configure AWS Amplify
import { awsConfig } from './config';

Amplify.configure(awsConfig);

function Login({ onLogin }: { onLogin: (user: any) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await signIn({ username, password });
      if (result && result.isSignedIn) {
        onLogin(result);
      } else {
        setError('Login failed: Invalid credentials.');
      }
    } catch (err: any) {
      // Cognito errors can be more specific
      let message = 'Login failed';
      if (err && err.message) {
        message = err.message;
      } else if (err && err.code === 'NotAuthorizedException') {
        message = 'Incorrect username or password.';
      } else if (err && err.code === 'UserNotFoundException') {
        message = 'User does not exist.';
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <form onSubmit={handleSubmit} className="bg-card p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="flex items-center mb-6">
          <div className="flex items-center justify-center w-12 h-12 bg-primary rounded-xl mr-4">
            <MessageCircle className="h-6 w-6 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">FlexKit Connect</h1>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Username</label>
          <input
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            autoFocus
            required
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            required
          />
        </div>
        {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-primary-foreground rounded-md h-10 font-semibold hover:bg-primary/90 transition-colors"
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>
    </div>
  );
}

interface Message {
  id: string
  phoneNumber: string
  timestamp: string
  message: string
  direction: 'inbound' | 'outbound'
  read: boolean
  twilioSid: string
  userId: string | null
  fromPhoneNumber?: string // Added for messages from the user's Twilio number
  // Call event fields
  type?: 'message' | 'call_initiated' | 'call_answered' | 'call_completed' | 'call_failed'
  callSid?: string
  callStatus?: string
  callDuration?: string
  callType?: 'browser' | 'proxy' // Distinguish between browser and proxy calls
  defaultDialedNumber?: string // For call events, this is the proxy number
  source?: 'api' | 'websocket' // Indicate if the message was sent via API or WebSocket
  keywordMatches?: string[] // Array of keyword IDs that matched this message
}

function App() {
  // Authentication state
  const [user, setUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  
  // App state
  const [messages, setMessages] = useState<Message[]>([])
  const [_allPhoneNumbers, setAllPhoneNumbers] = useState<string[]>([]) // Customer phone numbers
  const [userPhoneNumbers, setUserPhoneNumbers] = useState<Array<{phoneNumber: string, description: string | null}>>([]) // User's assigned Twilio numbers with descriptions
  const [selectedUserPhone, setSelectedUserPhone] = useState<string | undefined>(undefined) // Selected Twilio number to send from
  const [selectedPhone, setSelectedPhone] = useState<string | undefined>(undefined)
  const [newMessage, setNewMessage] = useState('')
  const [newPhoneNumber, setNewPhoneNumber] = useState('')
  const [sending, setSending] = useState(false)
  const [apiUrl, setApiUrl] = useState<string>('')
  const [composeOpen, setComposeOpen] = useState(false)
  const [wsConnectionStatus, setWsConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected')
  
  // Keyword state with localStorage persistence
  const [userKeywords, setUserKeywords] = useState<Array<{id: string, text: string}>>(() => {
    try {
      const stored = localStorage.getItem('userKeywords');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  })
  
  // Contact state
  const [contacts, setContacts] = useState<{[phoneNumber: string]: {name: string, email?: string}}>({})
  
  // Extract contact names from conversation data to avoid slow lookups
  const extractContactsFromConversations = useCallback((conversations: any[]) => {
    const extractedContacts: {[phoneNumber: string]: {name: string, email?: string}} = {}
    
    conversations.forEach(conv => {
      if (conv.contactName && conv.contactPhone) {
        extractedContacts[conv.contactPhone] = {
          name: conv.contactName,
          email: conv.contactEmail
        }
      }
    })
    
    setContacts(prev => ({ ...prev, ...extractedContacts }))
  }, [])
  
  // Unified call modal state
  const [callModalOpen, setCallModalOpen] = useState(false)
  const [autoSwitchToBrowserCall, setAutoSwitchToBrowserCall] = useState(false)
  
  // Browser call compose modal state
  const [browserCallComposeOpen, setBrowserCallComposeOpen] = useState(false)
  
  // Conversation unread counts from ConversationsView
  const [conversationUnreadCounts, setConversationUnreadCounts] = useState<{ [phoneNumber: string]: number }>({})
  
  // Mobile state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  
  // Contact loading state for WebSocket smart contact fetching
  const [loadingContacts, setLoadingContacts] = useState<Set<string>>(new Set());
  
  // Mobile/Tablet detection state
  const [isMobile, setIsMobile] = useState(false);
  
  // Active tab state ('conversations' or 'voicemails')
  const [activeTab, setActiveTab] = useState<'conversations' | 'voicemails'>('conversations');
  
  useEffect(() => {
    const checkMobile = () => {
      const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const smallScreen = window.innerWidth < 1280; // Block tablets too
      setIsMobile(mobileUA || smallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Authentication handlers
  useEffect(() => {
    getCurrentUser()
      .then(u => {
        setUser(u);
      })
      .catch((_error) => {
        setUser(null);
      });
  }, []);

  const handleLogin = async (u: any) => {
    // Only set user if isSignedIn is true (successful login)
    if (u && u.isSignedIn) {
      setUser(u);
    }
  };

  const handleLogout = async () => {
    await signOut();
    setUser(null);
    // Clear URL parameters on logout to prevent stickiness
    const url = new URL(window.location.href);
    url.searchParams.delete('phone');
    window.history.replaceState({}, '', url.toString());
  };

  // Fetch user profile and phone numbers after login
  useEffect(() => {
    if (user && apiUrl) {
      const fetchProfile = async () => {
        try {
          // Get JWT token for authentication
          const session = await fetchAuthSession();
          const jwtToken = session.tokens?.idToken?.toString();
          
          if (!jwtToken) {
            return;
          }

          // Use the secure V2 endpoint with JWT authentication
          const res = await fetch(`${apiUrl}/app/profile`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${jwtToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (res.ok) {
            const data = await res.json();
            setUserProfile(data);
            
            // Extract phone numbers from profile (with descriptions)
            const phoneNumbers = data.phoneNumbers || [];
            setUserPhoneNumbers(phoneNumbers);
            
            // Auto-select first phone number if none selected
            if (!selectedUserPhone && phoneNumbers.length > 0) {
              setSelectedUserPhone(phoneNumbers[0].phoneNumber);
            }
          }
        } catch (error) {
          // Silent error handling
        }
      };
      fetchProfile();

      // Fetch user keywords
      const fetchKeywords = async () => {
        try {
          const session = await fetchAuthSession();
          const jwtToken = session.tokens?.idToken?.toString();
          
          if (!jwtToken) {
            return;
          }

          const res = await fetch(`${apiUrl}/app/keywords`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${jwtToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (res.ok) {
            const data = await res.json();
            const keywords = data.keywords || [];
            setUserKeywords(keywords);
            
            // Persist to localStorage for state recovery
            try {
              localStorage.setItem('userKeywords', JSON.stringify(keywords));
            } catch (error) {
              // localStorage might be full or unavailable, continue without it
            }
          }
        } catch (error) {
          // Silent error handling
        }
      };
      fetchKeywords();
    }
  }, [user, apiUrl]);

  // Refs for stable references
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const previousMessagesRef = useRef<Message[]>([])
  const messageCountRef = useRef(0)
  const selectedPhoneRef = useRef<string | undefined>(selectedPhone)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 10
  const connectionIdRef = useRef<string | null>(null)
  const replyInputRef = useRef<HTMLInputElement>(null)
  const composeTextareaRef = useRef<HTMLTextAreaElement>(null)



  // Get the user's Twilio number from the first message that has a userId
  const userTwilioNumber = useMemo(() => {
    const userMessage = messages.find(msg => msg.userId);
    if (userMessage) {
      // For outbound messages with userId, fromPhoneNumber is the user's Twilio number
      // For inbound messages with userId, phoneNumber is the user's Twilio number
      const twilioNumber = userMessage.direction === 'outbound' ? userMessage.fromPhoneNumber : userMessage.phoneNumber;
      return twilioNumber;
    }
    return null;
  }, [messages]);

  // Utility to extract customer number from a message
  function getCustomerNumber(msg: Message, _userTwilioNumber: string | null): string {
    // For inbound, customer is fromPhoneNumber
    if (msg.direction === 'inbound') return msg.fromPhoneNumber || msg.phoneNumber;
    // For outbound, customer is phoneNumber
    if (msg.direction === 'outbound') return msg.phoneNumber;
    // fallback
    return msg.phoneNumber;
  }



  // Group messages by conversation (customer phone number)
  const conversationGroups = useMemo(() => {
    const groups: { [key: string]: Message[] } = {};
    
    messages.forEach(msg => {
      // Filter by selected user phone number if one is selected
      if (selectedUserPhone) {
        // Show messages where the user's phone number is involved in any way:
        // - Inbound: phoneNumber matches (messages TO the user's phone)
        // - Outbound: fromPhoneNumber matches (messages FROM the user's phone)
        // - Also show messages where the user's phone appears in fromPhoneNumber for inbound
        const isUserInvolved = 
          (msg.direction === 'inbound' && msg.phoneNumber === selectedUserPhone) ||
          (msg.direction === 'outbound' && msg.fromPhoneNumber === selectedUserPhone) ||
          (msg.direction === 'inbound' && msg.fromPhoneNumber === selectedUserPhone);
        
        if (!isUserInvolved) {
          return; // Skip this message
        }
      }
      
      const customerNumber = getCustomerNumber(msg, userTwilioNumber || null);
      if (!groups[customerNumber]) {
        groups[customerNumber] = [];
      }
      groups[customerNumber].push(msg);
    });
    
    // Sort messages within each conversation by timestamp
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    });
    
    return groups;
  }, [messages, userTwilioNumber, selectedUserPhone]);



  // Use conversation unread counts from ConversationsView (number of conversations with unread messages per phone)
  const twilioPhoneUnreadCounts = conversationUnreadCounts


  // Filter messages by selected phone number and sort by timestamp (memoized)
  const filteredMessages = useMemo(() => {
    if (!selectedPhone) {
      return [];
    }
    
    const conversationMessages = conversationGroups[selectedPhone] || [];
    return conversationMessages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [conversationGroups, selectedPhone])

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchMessages = useCallback(async (backgroundRefresh = false) => {
    if (!apiUrl || !user) {
      return;
    }
    
    // Loading state handled by ConversationsView component
    try {
      // Get JWT token
      let jwtToken: string | undefined;
      let session: any;
      try {
        session = await fetchAuthSession();
        jwtToken = session.tokens?.idToken?.toString();
        
        if (!jwtToken) {
          return;
        }
      } catch (error) {
        return;
      }
      
      // Call the V2 inbox API
      const url = backgroundRefresh 
        ? `${apiUrl}${API_ENDPOINTS.INBOX_V2}?timeFilterMinutes=10` 
        : `${apiUrl}${API_ENDPOINTS.INBOX_V2}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        const newMessages = data.messages || []
        
        if (backgroundRefresh) {
          // For background refresh, merge with existing messages instead of replacing
          setMessages(prevMessages => {
            // Create a map of existing messages by their unique ID
            const existingMessagesMap = new Map(prevMessages.map(msg => [`${msg.phoneNumber}-${msg.timestamp}`, msg]));
            
            // Add or update messages from the background refresh
            newMessages.forEach((newMsg: Message) => {
              const msgId = `${newMsg.phoneNumber}-${newMsg.timestamp}`;
              existingMessagesMap.set(msgId, newMsg);
            });
            
            // Convert back to array and sort by timestamp
            const mergedMessages = Array.from(existingMessagesMap.values())
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            
            return mergedMessages;
          });
        } else {
          // For regular refresh, replace all messages
          previousMessagesRef.current = newMessages
          messageCountRef.current = newMessages.length
          setMessages(newMessages)
        }

        if (!backgroundRefresh) {
          // Only update phone numbers and contacts for regular refreshes
          // Update user's phone numbers from V2 response
          if (data.userPhoneNumbers && Array.isArray(data.userPhoneNumbers)) {
            setUserPhoneNumbers(data.userPhoneNumbers)
            
            // Auto-select first phone number if none selected
            if (!selectedUserPhone && data.userPhoneNumbers.length > 0) {
              setSelectedUserPhone(data.userPhoneNumbers[0])
            }
          }

          // Update customer phone numbers from messages
          const uniquePhoneNumbers = [...new Set(newMessages.map((msg: Message) => msg.phoneNumber))]
          setAllPhoneNumbers(prev => {
            const combined = [...new Set([...prev, ...uniquePhoneNumbers])] as string[]
            return combined
          })

          // 🔍 Only fetch contacts for NEW phone numbers (not already in contacts state)
          const customerNumbers = [...new Set(newMessages.map((msg: Message) => {
            const customerNumber = getCustomerNumber(msg, userTwilioNumber || null);
            return customerNumber;
          }))];
          
          // Filter to only numbers we don't already have contacts for
          const newCustomerNumbers = customerNumbers.filter(num => !contacts[num as string] && num) as string[];
        
        
        // 🚀 Sort by most recent message timestamp to get the newest 100
        const sortedByRecency = newCustomerNumbers.sort((a, b) => {
          const aLatestMsg = newMessages
            .filter((msg: Message) => getCustomerNumber(msg, userTwilioNumber || null) === a)
            .sort((x: Message, y: Message) => new Date(y.timestamp).getTime() - new Date(x.timestamp).getTime())[0];
          const bLatestMsg = newMessages
            .filter((msg: Message) => getCustomerNumber(msg, userTwilioNumber || null) === b)
            .sort((x: Message, y: Message) => new Date(y.timestamp).getTime() - new Date(x.timestamp).getTime())[0];
          
          return new Date(bLatestMsg.timestamp).getTime() - new Date(aLatestMsg.timestamp).getTime();
        });
        
        // 🚀 Performance: Limit to most recent 75 phone numbers initially
        const limitedCustomerNumbers = sortedByRecency.slice(0, 75);
        const remainingNumbers = sortedByRecency.slice(75);
        
        if (limitedCustomerNumbers.length > 0) {
          // Wait for contacts to be fetched before setting loading to false
          await fetchContacts(limitedCustomerNumbers);
        }
        
        // Contacts loaded (state managed by ConversationsView)
        
        // 🚀 Load remaining contacts in the background (non-blocking)
        if (remainingNumbers.length > 0) {
          // Use local variable instead of state since state update is async
          // Don't await this - let it run in background
          loadRemainingContactsInBatches(remainingNumbers);
        }
        }
      }
    } catch (error) {
      // Handle error silently
    } finally {
      // Loading state handled by ConversationsView component
    }
  }, [apiUrl, user, contacts])

  // Fetch contacts for phone numbers
  const fetchContacts = useCallback(async (phoneNumbers: string[]) => {
    if (!apiUrl || !user || phoneNumbers.length === 0) {
      return;
    }
    
    
    try {
      // Get JWT token
      let jwtToken: string | undefined;
      let session: any;
      try {
        session = await fetchAuthSession();
        jwtToken = session.tokens?.idToken?.toString();
        
        if (!jwtToken) {
          return;
        }
      } catch (error) {
        return;
      }

      // Call the contact resolution API
      const response = await fetch(`${apiUrl}/app/contacts/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({ phoneNumbers })
      });

      if (response.ok) {
        const data = await response.json();
        const resolvedContacts = data.contacts || {};
        
        
        setContacts(prev => {
          const updatedContacts = { ...prev, ...resolvedContacts }
          return updatedContacts
        });
      } else {
      }
    } catch (error) {
    }
  }, [apiUrl, user])

  // Smart on-demand contact fetching with loop prevention
  const fetchContactIfNeeded = useCallback(async (phoneNumber: string) => {
    // 🛡️ Prevent loops: Check if already loaded or currently loading
    if (contacts[phoneNumber] || loadingContacts.has(phoneNumber)) {
      return;
    }
    
    // Add to loading set to prevent duplicate requests
    setLoadingContacts(prev => new Set(prev).add(phoneNumber));
    
    try {
      await fetchContacts([phoneNumber]);
    } finally {
      // Remove from loading set
      setLoadingContacts(prev => {
        const newSet = new Set(prev);
        newSet.delete(phoneNumber);
        return newSet;
      });
    }
  }, [contacts, loadingContacts, fetchContacts])


  // Load remaining contacts in batches of 300 for better performance
  const loadRemainingContactsInBatches = useCallback(async (phoneNumbers: string[]) => {
    const BATCH_SIZE = 300;
    const batches = [];
    
    // Split phone numbers into batches of 300
    for (let i = 0; i < phoneNumbers.length; i += BATCH_SIZE) {
      batches.push(phoneNumbers.slice(i, i + BATCH_SIZE));
    }
    
    
    // Process each batch with a small delay between batches to avoid overwhelming the API
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      
      try {
        await fetchContacts(batch);
        
        // Small delay between batches to be nice to the API
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`Error loading batch ${i + 1}:`, error);
        // Continue with next batch even if one fails
      }
    }
    
  }, [fetchContacts])

  // Enhanced auto-refresh with better interval management
  useEffect(() => {
    setApiUrl(API_BASE_URL)
  }, [])

  // Update ref when selectedPhone changes
  useEffect(() => {
    selectedPhoneRef.current = selectedPhone
  }, [selectedPhone])

  // URL query string handling
  const getPhoneFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('phone') || undefined
  }, [])

  const getCallFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('call') || undefined
  }, [])

  const updateUrlWithPhone = useCallback((phone: string | undefined) => {
    const url = new URL(window.location.href)
    if (phone) {
      url.searchParams.set('phone', phone)
    } else {
      url.searchParams.delete('phone')
    }
    window.history.replaceState({}, '', url.toString())
  }, [])

  const clearCallFromUrl = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete('call')
    window.history.replaceState({}, '', url.toString())
  }, [])

  // Initialize selected phone from URL on page load
  useEffect(() => {
    const phoneFromUrl = getPhoneFromUrl()
    
    if (phoneFromUrl) {
      // Normalize the phone number from URL to match existing conversation format
      const normalizedPhone = normalizePhoneNumber(phoneFromUrl)
      setSelectedPhone(normalizedPhone)
    }
  }, [getPhoneFromUrl])

  // Handle call parameter from URL
  useEffect(() => {
    const callFromUrl = getCallFromUrl()
    
    if (callFromUrl && user && userProfile) {
      // Normalize the phone number from URL
      const normalizedPhone = normalizePhoneNumber(callFromUrl)
      
      // Set the selected phone to the call target
      setSelectedPhone(normalizedPhone)
      
      // Set auto-switch flag and open the call modal
      setAutoSwitchToBrowserCall(true)
      setCallModalOpen(true)
      
      // Clear the call parameter from URL
      clearCallFromUrl()
      
    }
  }, [getCallFromUrl, user, userProfile, clearCallFromUrl])

  // Update URL when selectedPhone changes
  useEffect(() => {
    updateUrlWithPhone(selectedPhone)
  }, [selectedPhone, updateUrlWithPhone])

  // Calculate number of conversations with unread messages for selected phone
  const conversationsWithUnread = useMemo(() => {
    // If a phone is selected, show count for that phone only (0 if none)
    if (selectedUserPhone) {
      return conversationUnreadCounts[selectedUserPhone] || 0;
    }
    // If no phone selected, sum up all unread counts across all phone numbers
    return Object.values(conversationUnreadCounts).reduce((sum, count) => sum + count, 0);
  }, [conversationUnreadCounts, selectedUserPhone]);

  // Update page title with unread conversation count
  useEffect(() => {
    if (conversationsWithUnread > 0) {
      document.title = `FlexKit Connect (${conversationsWithUnread})`
    } else {
      document.title = 'FlexKit Connect'
    }
  }, [conversationsWithUnread])

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const phoneFromUrl = getPhoneFromUrl()
      
      if (phoneFromUrl) {
        // Normalize the phone number from URL to match existing conversation format
        const normalizedPhone = normalizePhoneNumber(phoneFromUrl)
        setSelectedPhone(normalizedPhone)
      } else {
        setSelectedPhone(phoneFromUrl)
      }
      // Don't fetch filtered messages - we want to keep all conversations visible
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [getPhoneFromUrl])

  // Auto-refresh disabled - ConversationsView handles its own refresh
  // useEffect(() => {
  //   autoRefreshInterval.current = setInterval(() => {
  //     if (!document.hidden && apiUrl) {
  //       fetchMessages(true)
  //     }
  //   }, 60000)
  //   
  //   return () => {
  //     if (autoRefreshInterval.current) {
  //       clearInterval(autoRefreshInterval.current)
  //     }
  //   }
  // }, [apiUrl])

  // Initial fetch disabled - ConversationsView handles its own data loading
  // useEffect(() => {
  //   if (apiUrl && user) {
  //     fetchMessages()
  //   }
  // }, [apiUrl, user])



  // WebSocket connection effect
  useEffect(() => {
    // Only connect WebSocket if user is authenticated
    if (!user) {
      return;
    }

    let manuallyClosed = false;
    const wsUrl = WS_URL;
    const apiUrl = API_BASE_URL;
    setApiUrl(apiUrl);

    const getReconnectDelay = (attempts: number) => {
      return Math.min(1000 * Math.pow(2, attempts), 30000); // Exponential backoff, max 30s
    };

    const connectWS = () => {
      if (manuallyClosed) return;
      
      // Generate unique connection ID to prevent multiple instances
      const currentConnectionId = `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      connectionIdRef.current = currentConnectionId;
      
      setWsConnectionStatus('connecting');

      try {
        // 🔑 Get user ID for WebSocket authentication
        let userId = user.userId || user.username || user.attributes?.sub;
        if (!userId && user.signInDetails?.loginId) userId = user.signInDetails.loginId;
        
        // Add userId as query parameter for WebSocket connection
        const wsUrlWithAuth = userId ? `${wsUrl}?userId=${encodeURIComponent(userId)}` : wsUrl;
        const ws = new WebSocket(wsUrlWithAuth);
        wsRef.current = ws;

        // Set connection timeout
        const connectionTimeout = setTimeout(() => {
          ws.close();
        }, 10000); // 10 second timeout

        ws.onopen = () => {
          clearTimeout(connectionTimeout);
          setWsConnectionStatus('connected');
          reconnectAttemptsRef.current = 0;
        };

        ws.onmessage = (event) => {
          // Only process messages if this is still the current connection
          if (connectionIdRef.current !== currentConnectionId) {
            return;
          }
          
          try {
            const data = JSON.parse(event.data);
            
            // Handle ping/pong for keep-alive
            if (data.type === 'ping') {
              ws.send(JSON.stringify({ type: 'pong' }));
              return;
            }

            // Handle different message types
            if (data.type === 'messages_marked_read') {
              // Handle messages marked as read
              const { phoneNumber } = data.data;
              
              // Update local state to reflect read status
              setMessages(prev => prev.map(msg => {
                const customerNumber = getCustomerNumber(msg, userTwilioNumber || null);
                return customerNumber === phoneNumber && 
                  msg.direction === 'inbound' && 
                  !msg.read 
                    ? { ...msg, read: true } 
                    : msg;
              }));
            }
            
            if (data.type === 'messages_marked_unread') {
              // Handle messages marked as unread
              const { phoneNumber } = data.data;
              
              // Update local state to reflect unread status
              setMessages(prev => prev.map(msg => {
                const customerNumber = getCustomerNumber(msg, userTwilioNumber || null);
                return customerNumber === phoneNumber && 
                  msg.direction === 'inbound' && 
                  msg.read === true 
                    ? { ...msg, read: false } 
                    : msg;
              }));
            } else if (data.type === 'call_status_update') {
              // Handle call status updates
              const callEvent = data.data;
              
              // Convert call event to message format
              const callMessage: Message = {
                id: `call-${callEvent.callSid}-${callEvent.timestamp}`,
                phoneNumber: callEvent.toNumber, // Customer number
                fromPhoneNumber: callEvent.fromNumber,
                timestamp: callEvent.timestamp,
                message: '', // Call events don't have message content
                direction: 'outbound' as const,
                read: true,
                twilioSid: callEvent.callSid,
                userId: null,
                type: `call_${callEvent.callStatus.toLowerCase()}` as any,
                callSid: callEvent.callSid,
                callStatus: callEvent.callStatus,
                callDuration: callEvent.duration,
                callType: callEvent.callType || 'proxy', // Default to proxy if not specified
                defaultDialedNumber: callEvent.toNumber
              };
              
              setMessages(prev => {
                // Avoid duplicates
                if (prev.some(m => m.id === callMessage.id)) return prev;
                const updated = [...prev, callMessage].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                setTimeout(() => scrollToBottom(), 100);
                return updated;
              });
            } else {
              // Accept either a single message or a wrapper { data: message }
              const newMsg = data.data || data;
              // Only add real SMS messages (not call events)
              if (
                newMsg &&
                newMsg.phoneNumber &&
                newMsg.timestamp &&
                (newMsg.direction === 'inbound' || newMsg.direction === 'outbound') &&
                !newMsg.type // Regular messages don't have type field
              ) {
                // Keyword matching handled by ConversationsView
                // if (newMsg.direction === 'inbound') {
                //   setTimeout(() => {
                //     fetchMessages(true);
                //   }, 2000);
                // }
                
                setMessages(prev => {
                  // Avoid duplicates
                  if (prev.some(m => m.twilioSid === newMsg.twilioSid && newMsg.twilioSid)) return prev;
                  const updated = [...prev, newMsg].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                  // Auto-scroll to bottom after state update
                  setTimeout(() => scrollToBottom(), 100);
                  return updated;
                });
                // Update all phone numbers when new message arrives
                setAllPhoneNumbers(prev => {
                  if (!prev.includes(newMsg.phoneNumber)) {
                    return [...prev, newMsg.phoneNumber];
                  }
                  return prev;
                });
                
                // 🔍 Smart contact loading: Fetch contact for new message if we don't have it
                const customerNumber = getCustomerNumber(newMsg, userTwilioNumber || null);
                if (!contacts[customerNumber]) {
                  fetchContactIfNeeded(customerNumber);
                }
                
                // Message received via WebSocket
              }
            }
          } catch (e) {
            // Handle parse error silently
          }
        };

        ws.onclose = (_event) => {
          clearTimeout(connectionTimeout);
          wsRef.current = null;
          
          if (manuallyClosed) {
            setWsConnectionStatus('disconnected');
            return;
          }

          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = getReconnectDelay(reconnectAttemptsRef.current);
            setWsConnectionStatus('disconnected');
            
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current++;
              connectWS();
            }, delay);
          } else {
            setWsConnectionStatus('error');
          }
        };

        ws.onerror = (_error) => {
          clearTimeout(connectionTimeout);
          setWsConnectionStatus('error');
          
          // Don't immediately close - let onclose handle reconnection
          // This prevents rapid reconnection attempts on network errors
        };

      } catch (error) {
        setWsConnectionStatus('error');
        
        // Retry connection after delay
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = getReconnectDelay(reconnectAttemptsRef.current);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connectWS();
          }, delay);
        }
      }
    };

    // Handle manual reconnect events
    const handleManualReconnectEvent = () => {
      reconnectAttemptsRef.current = 0
      connectWS()
    }

    // Initial connection
    connectWS();

    // Listen for manual reconnect events
    window.addEventListener('manual-ws-reconnect', handleManualReconnectEvent)

    // Cleanup function
    return () => {
      manuallyClosed = true;
      window.removeEventListener('manual-ws-reconnect', handleManualReconnectEvent)
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [user]); // Add user as dependency



  // Auto-scroll to bottom function
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Auto-scroll when conversation is selected or messages change
  useEffect(() => {
    if (selectedPhone && filteredMessages.length > 0) {
      setTimeout(() => scrollToBottom(), 100)
    }
  }, [selectedPhone, filteredMessages.length])



  // Manual WebSocket reconnect function
  const handleManualReconnect = () => {
    reconnectAttemptsRef.current = 0 // Reset attempts
    
    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual reconnect')
    }
    
    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    
    // Trigger reconnection by dispatching a custom event
    // This will be handled by the WebSocket useEffect
    window.dispatchEvent(new CustomEvent('manual-ws-reconnect'))
  }

  const sendMessage = async () => {
    // Determine recipient: if compose modal is open, use newPhoneNumber, otherwise use selectedPhone
    const messageToSend = composeOpen ? newPhoneNumber : selectedPhone
    if (!newMessage.trim() || !messageToSend?.trim() || !user) return
    
    // Type guard to ensure messageToSend is not undefined
    if (!messageToSend) return
    
    // Normalize phone number to E.164 format
    const normalizedPhoneNumber = normalizePhoneNumber(messageToSend)
    
    setSending(true)
    try {
      // Get JWT token for v6
      let jwtToken: string | undefined;
      let session: any;
      try {
        session = await fetchAuthSession();
        jwtToken = session.tokens?.idToken?.toString();
        
        if (!jwtToken) {
          return;
        }
      } catch (error) {
        return;
      }
      
      // Call send message API
      const response = await fetch(`${apiUrl}/app/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          to: normalizedPhoneNumber,
          message: newMessage,
          fromPhoneNumber: selectedUserPhone, // Include selected Twilio number
        })
      })
      if (response.ok) {
        const data = await response.json()
        // Add the sent message to local state
        const newMsg: Message = {
          id: `outbound-${Date.now()}`,
          phoneNumber: normalizedPhoneNumber,
          fromPhoneNumber: selectedUserPhone || userTwilioNumber || '',
          timestamp: new Date().toISOString(),
          message: newMessage,
          direction: 'outbound',
          read: true,
          twilioSid: data.messageId || `SM${Math.random().toString(36).substr(2, 34)}`,
          userId: null,
          source: 'websocket' // Indicate it was sent via WebSocket
        }
        setMessages(prev => {
          const updated = [newMsg, ...prev]
          // Auto-scroll to bottom after sending message
          setTimeout(() => scrollToBottom(), 100)
          return updated
        })
        
        // Update all phone numbers when sending message
        setAllPhoneNumbers(prev => {
          if (!prev.includes(normalizedPhoneNumber)) {
            return [...prev, normalizedPhoneNumber];
          }
          return prev;
        })
        
        // 🔍 Smart contact loading: Fetch contact for new phone number if we don't have it
        if (!contacts[normalizedPhoneNumber]) {
          fetchContactIfNeeded(normalizedPhoneNumber);
        }
        
        setNewMessage('')
        if (composeOpen) {
          setNewPhoneNumber('')
          setComposeOpen(false)
        }
        // Always switch to the conversation we just sent to
        setSelectedPhone(normalizedPhoneNumber)
        updateUrlWithPhone(normalizedPhoneNumber)
        // Let WebSocket handle real-time updates instead of manual refresh
      } else {
        // Fallback to local simulation if API fails
        const newMsg: Message = {
          id: `outbound-${Date.now()}`,
          phoneNumber: normalizedPhoneNumber,
          timestamp: new Date().toISOString(),
          message: newMessage,
          direction: 'outbound',
          read: true,
          twilioSid: `SM${Math.random().toString(36).substr(2, 34)}`,
          userId: null,
          source: 'websocket' // Indicate it was sent via WebSocket
        }
        setMessages(prev => {
          const updated = [newMsg, ...prev]
          // Auto-scroll to bottom after sending message
          setTimeout(() => scrollToBottom(), 100)
          return updated
        })
        
        // Update all phone numbers when sending message
        setAllPhoneNumbers(prev => {
          if (!prev.includes(normalizedPhoneNumber)) {
            return [...prev, normalizedPhoneNumber];
          }
          return prev;
        })
        
        // 🔍 Smart contact loading: Fetch contact for new phone number if we don't have it
        if (!contacts[normalizedPhoneNumber]) {
          fetchContactIfNeeded(normalizedPhoneNumber);
        }
        
        setNewMessage('')
        if (composeOpen) {
          setNewPhoneNumber('')
          setComposeOpen(false)
        }
        // Always switch to the conversation we just sent to
        setSelectedPhone(normalizedPhoneNumber)
        updateUrlWithPhone(normalizedPhoneNumber)
      }
    } catch (error) {
      // Fallback to local simulation
      const newMsg: Message = {
        id: `outbound-${Date.now()}`,
        phoneNumber: normalizedPhoneNumber,
        timestamp: new Date().toISOString(),
        message: newMessage,
        direction: 'outbound',
        read: true,
        twilioSid: `SM${Math.random().toString(36).substr(2, 34)}`,
        userId: null,
        source: 'websocket' // Indicate it was sent via WebSocket
      }
      setMessages(prev => {
        const updated = [newMsg, ...prev]
        // Auto-scroll to bottom after sending message
        setTimeout(() => scrollToBottom(), 100)
        return updated
      })
      
      // Update all phone numbers when sending message
      setAllPhoneNumbers(prev => {
        if (!prev.includes(normalizedPhoneNumber)) {
          return [...prev, normalizedPhoneNumber];
        }
        return prev;
      })
      
      // 🔍 Smart contact loading: Fetch contact for new phone number if we don't have it
      if (!contacts[normalizedPhoneNumber]) {
        fetchContactIfNeeded(normalizedPhoneNumber);
      }
      
      setNewMessage('')
      if (composeOpen) {
        setNewPhoneNumber('')
        setComposeOpen(false)
      }
      // Always switch to the conversation we just sent to
      setSelectedPhone(normalizedPhoneNumber)
      updateUrlWithPhone(normalizedPhoneNumber)
    } finally {
      setSending(false)
    }
  }

  // const markAsRead = async (messageId: string) => {
  //   // TODO: Implement mark as read functionality
  //   
  //   // Update local state
  //   setMessages(prev => prev.map(msg => 
  //     msg.id === messageId ? { ...msg, read: true } : msg
  //   ))
  // }

  // const handleReply = (message: Message) => {
  //   setReplyToMessage(message)
  //   setNewMessage('')
  // }


  const normalizePhoneNumber = (input: string) => {
    // Remove all non-digit characters
    const digits = (input || '').replace(/\D/g, '');
    // If already E.164, return as is
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`; // for India, example
    // fallback: return as is
    return input;
  }

  const formatPhoneNumber = (phone: string) => {
    // Simple formatting - in production, use a proper phone number library
    return phone.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1-$2-$3-$4')
  }




  const handleComposeClick = () => {
    setComposeOpen(true)
    // Clear the phone number field to start fresh
    setNewPhoneNumber('')
    // Also clear the message field
    setNewMessage('')
  }

  const handleBrowserCallComposeClick = () => {
    setBrowserCallComposeOpen(true)
  }

  const handleEmojiSelect = (emoji: string) => {
    // Try to determine which input to update based on context
    let targetInput: HTMLInputElement | HTMLTextAreaElement | null = null;
    
    // Check if compose modal is open
    if (composeOpen && composeTextareaRef.current) {
      targetInput = composeTextareaRef.current;
    } else if (replyInputRef.current) {
      targetInput = replyInputRef.current;
    }
    
    if (targetInput) {
      const start = targetInput.selectionStart ?? 0;
      const end = targetInput.selectionEnd ?? 0;
      const value = targetInput.value;
      
      // Insert emoji at cursor position
      const newValue = value.substring(0, start) + emoji + value.substring(end);
      
      // Update the React state
      setNewMessage(newValue);
      
      // Set cursor position after the inserted emoji
      const newCursorPos = start + emoji.length;
      setTimeout(() => {
        targetInput!.setSelectionRange(newCursorPos, newCursorPos);
        targetInput!.focus();
      }, 0);
    }
  }

  const handleTemplateSelect = (content: string) => {
    // Try to determine which input to update based on context
    let targetInput: HTMLInputElement | HTMLTextAreaElement | null = null;
    
    // Check if compose modal is open
    if (composeOpen && composeTextareaRef.current) {
      targetInput = composeTextareaRef.current;
    } else if (replyInputRef.current) {
      targetInput = replyInputRef.current;
    }
    
    if (targetInput) {
      // Update the React state
      setNewMessage(content);
      
      // Focus back to the input
      setTimeout(() => {
        targetInput!.focus();
      }, 0);
    }
  }

  const handleTemplateCreate = async (name: string, content: string) => {
    if (!apiUrl || !user) return;

    try {
      // Get JWT token
      const session = await fetchAuthSession();
      const jwtToken = session.tokens?.idToken?.toString();
      
      if (!jwtToken) {
        return;
      }

      const response = await fetch(`${apiUrl}/app/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({ name, content })
      });

      if (!response.ok) {
        throw new Error('Failed to create template');
      }
    } catch (error) {
      throw error;
    }
  }



  // Handle call button click
  const handleCallClick = (customerNumber: string) => {
    setSelectedPhone(customerNumber);
    setCallModalOpen(true);
  }

  // Mark messages as read when conversation is opened
  const markMessagesAsRead = useCallback(async (customerNumber: string) => {
    if (!apiUrl || !user) {
      return;
    }

    try {
      
      // Get JWT token
      let jwtToken: string | undefined;
      let session: any;
      try {
        session = await fetchAuthSession();
        jwtToken = session.tokens?.idToken?.toString();
        
        if (!jwtToken) {
          return;
        }
      } catch (error) {
        return;
      }

      const response = await fetch(`${apiUrl}/app/messages/mark-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          customerNumber,
          fromPhoneNumber: selectedUserPhone // Include the selected Twilio number for filtering
        })
      });

      if (response.ok) {
        await response.json();
        // Update local state instead of refetching all messages
        setMessages(prevMessages => 
          prevMessages.map(msg => {
            const msgCustomerNumber = getCustomerNumber(msg, userTwilioNumber || null);
            if (msgCustomerNumber === customerNumber && msg.direction === 'inbound') {
              return { ...msg, read: true };
            }
            return msg;
          })
        );
      }
    } catch (error) {
      // Silent error handling
    }
  }, [apiUrl, user, fetchMessages, selectedUserPhone]);

  // Track the last selected phone to prevent duplicate calls
  const lastSelectedPhoneRef = useRef<string | null>(null);
  const autoMarkReadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-mark inbound messages as read when a conversation is selected
  useEffect(() => {
    if (selectedPhone && selectedPhone !== lastSelectedPhoneRef.current) {
      lastSelectedPhoneRef.current = selectedPhone;
      
      // Clear any existing timeout
      if (autoMarkReadTimeoutRef.current) {
        clearTimeout(autoMarkReadTimeoutRef.current);
        autoMarkReadTimeoutRef.current = null;
      }
      
      // Check if there are unread inbound messages for this conversation
      const conversationMessages = messages.filter(msg => {
        const customerNumber = getCustomerNumber(msg, userTwilioNumber || null);
        return customerNumber === selectedPhone;
      })
      const inboundMessages = conversationMessages.filter(msg => msg.direction === 'inbound')
      const unreadInboundMessages = inboundMessages.filter(msg => !msg.read)
      
      if (unreadInboundMessages.length > 0) {
        // Mark as read immediately when conversation is selected
        markMessagesAsRead(selectedPhone);
        
        // Also set a backup timeout to mark as read after 2 seconds of viewing
        // This ensures messages are marked as read even if the immediate call fails
        autoMarkReadTimeoutRef.current = setTimeout(() => {
          markMessagesAsRead(selectedPhone);
        }, 2000);
      }
    }
    
    // Cleanup timeout on unmount or when selectedPhone changes
    return () => {
      if (autoMarkReadTimeoutRef.current) {
        clearTimeout(autoMarkReadTimeoutRef.current);
        autoMarkReadTimeoutRef.current = null;
      }
    };
  }, [selectedPhone, messages, markMessagesAsRead])


  // 🚫 Removed: No longer fetch contacts on every phone number change
  // Contacts are now fetched only when new numbers are encountered

  // 🚫 Removed: No longer auto-refresh every 10 seconds
  // Now using 60-second interval in the main auto-refresh effect

  // Show mobile blocker for mobile/tablet devices (< 1280px)
  if (isMobile) {
    return <MobileBlocker />;
  }

  // Show login if not authenticated
  if (!user) {
    // Don't clear URL parameters during authentication - preserve them for after login
    return <Login onLogin={handleLogin} />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 sm:p-6 max-w-6xl">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-xl">
                <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold tracking-tight">FlexKit Connect</h1>
                {userProfile && (
                  <p className="text-sm text-muted-foreground hidden sm:block">
                    Welcome, {userProfile.givenName || 'User'} • {userProfile.email}
                  </p>
                )}
              </div>
            </div>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>

            {/* Desktop header actions */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Phone Number Selector */}
              {userPhoneNumbers.length > 1 && (
                <PhoneSelector
                  phoneNumbers={userPhoneNumbers}
                  selectedPhone={selectedUserPhone}
                  onSelect={setSelectedUserPhone}
                  unreadCounts={twilioPhoneUnreadCounts}
                  formatPhoneNumber={formatPhoneNumber}
                />
              )}

              <button 
                onClick={handleComposeClick}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 w-10"
                title="New Message"
              >
                <MessageCircle className="h-4 w-4" />
              </button>

              <button 
                onClick={handleBrowserCallComposeClick}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 w-10"
                title="Call"
              >
                <Phone className="h-4 w-4" />
              </button>

              <KeywordPickerV2
                apiUrl={apiUrl}
                user={user}
                className="h-10 px-3"
              />

              {/* Connection Status - Same height as other elements */}
              <div className="flex items-center justify-center h-10 w-10 rounded-md bg-muted/50 border border-input" title={
                wsConnectionStatus === 'connected' ? 'Connected' :
                wsConnectionStatus === 'connecting' ? 'Connecting...' :
                wsConnectionStatus === 'disconnected' ? 'Reconnecting...' :
                'Connection Error'
              }>
                <div className={`w-2 h-2 rounded-full ${
                  wsConnectionStatus === 'connected' ? 'bg-green-500' :
                  wsConnectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                  wsConnectionStatus === 'disconnected' ? 'bg-orange-500' :
                  'bg-red-500'
                }`} />
              </div>

              <button 
                onClick={handleLogout}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 w-10"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 p-4 bg-card border rounded-lg shadow-sm">
              <div className="space-y-3">
                {/* Connection Status - Simple */}
                <div className="flex items-center justify-end">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center h-10 w-10 rounded-md bg-muted/50 border border-input" title={
                      wsConnectionStatus === 'connected' ? 'Connected' :
                      wsConnectionStatus === 'connecting' ? 'Connecting...' :
                      wsConnectionStatus === 'disconnected' ? 'Reconnecting...' :
                      'Connection Error'
                    }>
                      <div className={`w-2 h-2 rounded-full ${
                        wsConnectionStatus === 'connected' ? 'bg-green-500' :
                        wsConnectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                        wsConnectionStatus === 'disconnected' ? 'bg-orange-500' :
                        'bg-red-500'
                      }`} />
                    </div>
                    {wsConnectionStatus === 'error' && (
                      <button
                        onClick={handleManualReconnect}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  {/* Phone Number Selector - Mobile */}
                  {userPhoneNumbers.length > 1 && (
                    <PhoneSelector
                      phoneNumbers={userPhoneNumbers}
                      selectedPhone={selectedUserPhone}
                      onSelect={setSelectedUserPhone}
                      unreadCounts={twilioPhoneUnreadCounts}
                      formatPhoneNumber={formatPhoneNumber}
                    />
                  )}
                  
                  <button 
                    onClick={handleComposeClick}
                    className="flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-3"
                    title="New Message"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </button>
                </div>
                
                <button 
                  onClick={handleBrowserCallComposeClick}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-3"
                  title="Call"
                >
                  <Phone className="h-4 w-4" />
                </button>
                
                <button 
                  onClick={handleLogout}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-3"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="mb-4 border-b">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('conversations')}
              className={`inline-flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === 'conversations'
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              }`}
            >
              <MessageCircle className="h-4 w-4" />
              Conversations
              {conversationsWithUnread > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-primary-foreground bg-primary rounded-full">
                  {conversationsWithUnread}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('voicemails')}
              data-tab="voicemails"
              className={`inline-flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === 'voicemails'
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              }`}
            >
              <Voicemail className="h-4 w-4" />
              Voicemails
            </button>
          </div>
        </div>

        {/* Conversations View */}
        {activeTab === 'conversations' && (
          <ConversationsView
            apiUrl={apiUrl}
            user={user}
            userProfile={userProfile}
            selectedUserPhone={selectedUserPhone}
            userPhoneNumbers={userPhoneNumbers}
            onCallClick={handleCallClick}
            wsMessages={messages}
            contacts={contacts}
            fetchContactIfNeeded={fetchContactIfNeeded}
            userKeywords={userKeywords}
            onUnreadCountsChange={setConversationUnreadCounts}
            onConversationsLoaded={extractContactsFromConversations}
          />
        )}

        {/* Voicemails View */}
        {activeTab === 'voicemails' && (
          <VoicemailsView
            apiUrl={apiUrl}
            user={user}
            selectedUserPhone={selectedUserPhone}
            contacts={contacts}
            onCallClick={handleCallClick}
          />
        )}
        {/* Enhanced Compose Message Modal */}
        {composeOpen && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="rounded-lg border bg-card text-card-foreground shadow-lg w-full max-w-md">
              <div className="flex flex-col space-y-1.5 p-4 sm:p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold leading-none tracking-tight">Send New Message</h3>
                  <button
                    onClick={() => setComposeOpen(false)}
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value)}
                    placeholder="+1234567890"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Message
                  </label>
                  <div className="relative">
                    <textarea
                      ref={composeTextareaRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      rows={4}
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.metaKey) {
                          sendMessage()
                        }
                      }}
                    />
                    <div className="absolute top-2 right-2 flex space-x-1">
                      <EmojiPickerButton
                        onEmojiSelect={handleEmojiSelect}
                        className="h-10 w-10 p-2"
                      />
                      <TemplatePicker
                        onTemplateSelect={handleTemplateSelect}
                        onTemplateCreate={handleTemplateCreate}
                        className="h-10 w-10 p-2"
                        apiUrl={apiUrl}
                        user={user}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Press Cmd+Enter to send
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setComposeOpen(false)}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => sendMessage()}
                    disabled={sending || !newMessage.trim() || !newPhoneNumber.trim()}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4"
                  >
                    {sending ? (
                      <LoadingSpinner size="sm" hideText />
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Configuration Notice - only show if not configured */}
        {!apiUrl && (
          <div className="mt-6">
            <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
              <div className="p-6">
                <div className="rounded-lg border bg-yellow-50 border-yellow-200 p-4">
                  <h3 className="font-semibold text-yellow-800 mb-2">Configuration Required</h3>
                  <p className="text-yellow-700 text-sm">
                    To use this inbox, you need to:
                  </p>
                  <ul className="text-yellow-700 text-sm mt-2 space-y-1">
                    <li>• Deploy the CDK stack to AWS</li>
                    <li>• Configure your Twilio webhook URL</li>
                    <li>• Update the API URL in the frontend</li>
                    <li>• Set up authentication (future feature)</li>
                    <li>• Auto-refresh enabled (5 second intervals)</li>
                    <li>• Modern shadcn/ui styling</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Unified Call Modal */}
        {callModalOpen && selectedPhone && (
          <CallModal
            isOpen={callModalOpen}
            onClose={() => {
              setCallModalOpen(false)
              setAutoSwitchToBrowserCall(false)
            }}
            customerNumber={selectedPhone}
            apiUrl={apiUrl}
            user={user}
            userProfile={userProfile}
            selectedUserPhone={selectedUserPhone}
            autoSwitchToBrowserCall={autoSwitchToBrowserCall}
          />
        )}

        {/* Browser Call Compose Modal */}
        {browserCallComposeOpen && (
          <BrowserCallComposeModal
            isOpen={browserCallComposeOpen}
            onClose={() => setBrowserCallComposeOpen(false)}
            apiUrl={apiUrl}
            user={user}
            selectedUserPhone={selectedUserPhone}
          />
        )}

      </div>
    </div>
    </ErrorBoundary>
  )
}

export default App 